import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ChatCompletionService from "@/services/openai/chatCompletionService";
import { BotIcon, SendHorizontalIcon, UserIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

// Extracting common styles to reduce boilerplate
const STYLES = {
  modal:
    "fixed bottom-20 right-4 z-50 w-[350px] sm:w-[400px] h-[500px] rounded-xl border bg-background shadow-lg flex flex-col transition-all duration-300 backdrop-blur-sm",
  avatar: "rounded-full flex items-center justify-center flex-shrink-0",
  botAvatar: "bg-primary/10",
  userAvatar: "bg-primary text-primary-foreground",
  message: "max-w-[80%] rounded-2xl px-4 py-2",
  userMessage: "bg-primary text-primary-foreground rounded-tr-sm",
  botMessage: "bg-muted text-foreground rounded-tl-sm",
  input:
    "w-full resize-none bg-muted/50 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary pr-10 max-h-32",
  inputContainer: "p-1 relative flex-1 overflow-hidden",
  header: "flex items-center justify-between p-3 border-b",
  headerTitle: "flex items-center gap-2",
  closeButton: "h-8 w-8 rounded-full hover:bg-muted",
  messagesContainer: "flex-1 overflow-y-auto p-4 space-y-4",
  emptyState: "flex flex-col items-center justify-center h-full text-center",
  typingIndicator: "flex space-x-2 items-center h-5",
  typingDot: "h-2 w-2 rounded-full bg-current animate-bounce",
  inputForm: "flex gap-2 items-end",
  sendButton: "rounded-xl h-10 w-10",
  loadingText: "absolute right-2 bottom-3 text-xs text-muted-foreground",
};

export const AssistantModal = ({
  open,
  onOpenChange,
  title = "Assistant",
  description = "How can I help you?",
  welcomeMessage = "How can I help you today?",
  className = "",
  iconClassName = "",
  actionButtonClassName = "",
  apiConfig = {},
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const modalRef = useRef(null);

  // Handle outside clicks to close the modal
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open, onOpenChange]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Auto-resize textarea as user types
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Add user message
    const userMessage = { role: "user", content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setIsTyping(true);

    try {
      // Create empty assistant message that will be streamed into
      const pendingAssistantMessage = { role: "assistant", content: "" };
      setMessages((prev) => [...prev, pendingAssistantMessage]);

      // Create chat completion service instance
      const chatService = new ChatCompletionService();
      chatService.setSystemPrompt(
        apiConfig.systemMessage || "You are a helpful assistant."
      );

      // Prepare messages for the API
      const messagesToSend = [...messages, userMessage];

      // Call backend API for chat completion
      const response = await chatService.createChatCompletion(messagesToSend, {
        model: apiConfig.model || "gpt-4o",
        temperature: apiConfig.temperature || 0.7,
      });

      // Update the last message with the response content
      const assistantContent = response.choices[0].message.content;
      setMessages((prev) => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1].content = assistantContent;
        return newMessages;
      });
    } catch (error) {
      console.error("Error getting response from backend API:", error);
      setMessages((prev) => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1].content =
          "Sorry, I encountered an error. Please try again later.";
        return newMessages;
      });
    } finally {
      setIsLoading(false);
      setIsTyping(false);
      textareaRef.current && (textareaRef.current.style.height = "auto");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!open) return null;

  // Helper components to reduce boilerplate
  const BotAvatarIcon = ({ size = "h-5 w-5" }) => (
    <div
      className={cn(STYLES.avatar, STYLES.botAvatar, "h-8 w-8", iconClassName)}
    >
      <BotIcon className={cn(size, iconClassName)} />
    </div>
  );

  const renderMessages = () => {
    if (messages.length === 0) {
      return (
        <div className={STYLES.emptyState}>
          <div
            className={cn(
              STYLES.avatar,
              STYLES.botAvatar,
              "h-16 w-16 mb-4",
              iconClassName
            )}
          >
            <BotIcon className={cn("h-8 w-8", iconClassName)} />
          </div>
          <p className="font-medium mb-4">{welcomeMessage}</p>
          <p className="text-sm text-muted-foreground max-w-xs">
            Ask a question to get started
          </p>
        </div>
      );
    }

    return (
      <>
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex items-start gap-2 ${message.role === "user" ? "justify-end" : "justify-start"} animate-in slide-in-from-bottom-2 duration-300`}
          >
            {message.role !== "user" && <BotAvatarIcon />}

            <div
              className={cn(
                STYLES.message,
                message.role === "user" ? STYLES.userMessage : STYLES.botMessage
              )}
            >
              {message.role === "assistant" ? (
                <ReactMarkdown className="prose prose-sm dark:prose-invert max-w-none">
                  {message.content || " "}
                </ReactMarkdown>
              ) : (
                <div>{message.content}</div>
              )}
            </div>

            {message.role === "user" && (
              <div className={cn(STYLES.avatar, STYLES.userAvatar, "h-8 w-8")}>
                <UserIcon className="h-5 w-5" />
              </div>
            )}
          </div>
        ))}

        {isTyping && messages.length > 0 && (
          <div className="flex justify-start items-center gap-2">
            <BotAvatarIcon />
            <div className={cn(STYLES.message, STYLES.botMessage)}>
              <div className={STYLES.typingIndicator}>
                <div className={STYLES.typingDot} />
                <div
                  className={cn(STYLES.typingDot, "[animation-delay:0.2s]")}
                />
                <div
                  className={cn(STYLES.typingDot, "[animation-delay:0.4s]")}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={cn(STYLES.modal, className)} ref={modalRef}>
      {/* Header */}
      <div className={STYLES.header}>
        <div className={STYLES.headerTitle}>
          <BotAvatarIcon />
          <div>
            <h3 className="font-medium">{title}</h3>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onOpenChange(false)}
          className={STYLES.closeButton}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <div className={STYLES.messagesContainer}>
        {renderMessages()}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-3">
        <form onSubmit={handleSubmit} className={STYLES.inputForm}>
          <div className={STYLES.inputContainer}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className={STYLES.input}
              disabled={isLoading}
            />
            {isLoading && <div className={STYLES.loadingText}>Loading...</div>}
          </div>
          <Button
            type="submit"
            size="icon"
            disabled={isLoading || !input.trim()}
            className={cn(STYLES.sendButton, actionButtonClassName)}
          >
            <SendHorizontalIcon className="h-5 w-5" />
          </Button>
        </form>
      </div>
    </div>
  );
};
